"use client";

import React from "react";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";

/** Left padding that lines text up under the label of a `CoverageCheck` (box h-6/w-6 + gap-3). */
export const CHECK_INDENT = "pl-9";

/** The box of a checkbox: an empty, clearly outlined square when the coverage is not in, filled with a
 * white check mark when it is — so it reads as something to click, not as a status icon. */
export const CheckBoxMark: React.FC<{ checked: boolean; locked?: boolean }> = ({ checked, locked }) => (
  <span
    aria-hidden="true"
    className={cn(
      "flex h-6 w-6 shrink-0 items-center justify-center rounded-md border-2 transition-colors",
      locked
        ? "border-border bg-muted text-foreground/60"
        : cn("bg-background group-hover:border-primary", checked ? "border-primary bg-primary text-primary-foreground" : "border-primary/40")
    )}
  >
    {checked && <Check className="h-4 w-4" strokeWidth={3} />}
  </span>
);

/** Clickable checkbox row: box plus its text (`children`) are one button, so the whole label is the
 * click target, with hover and a visible focus ring. */
export const CoverageCheck: React.FC<{
  checked: boolean;
  disabled?: boolean;
  label: string;
  onChange: (value: boolean) => void;
  className?: string;
  children?: React.ReactNode;
}> = ({ checked, disabled, label, onChange, className, children }) => (
  <button
    type="button"
    role="checkbox"
    aria-checked={checked}
    aria-label={label}
    disabled={disabled}
    onClick={() => onChange(!checked)}
    className={cn(
      "group -m-1 flex min-w-0 items-center gap-3 rounded-md p-1 text-left focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring",
      disabled ? "cursor-not-allowed opacity-40" : "cursor-pointer hover:bg-primary/5",
      className
    )}
  >
    <CheckBoxMark checked={checked} />
    {children}
  </button>
);
